"use client";
import Link from "next/link";
import { useParams } from "next/navigation";
import React, { Dispatch, SetStateAction, useState } from "react";

import { ParkingDetailTableSortField } from "components";
import { EmptyTable } from "components/EmptyTable";
import FullSizeLoading from "components/FullSizeLoading";
import PublicTag from "components/PublicTag";
import { SORT_DESTINATION, SortDestination, SpaceGroupOrderFields } from "constants/sort";
import { LessorSpaceGroupsList, Maybe, SpaceGroupOrder } from "graphql/type";
import { calculateMonthlyRent } from "utils/calculateMonthlyRent";
import { ParkingType, convertParkingType } from "utils/filter/convertParkingType.util";
import { SupportCarModel, findLargestSupportCarModel } from "utils/filter/findLargestCarModel.util";
import { convertNumberToLocaleString } from "utils/string.util";

export type SpaceRowData = {
  numberOfSpace: number;
  price: number;
  parkingType: ParkingType;
  supportCarModel: SupportCarModel;
  remarks: string;
  isPublic: boolean;
  available: boolean;
  numberOfFree: number;
  numberOfFreeSchedule: number;
  full: number;
};

const tailwindPlaceCenter = "flex items-center px-4";

const genClass = (num?: Maybe<number>) => {
  return num === 0 ? `${tailwindPlaceCenter} font-normal !text-gray` : `${tailwindPlaceCenter} font-bold`;
};

const createIndention = (supportCarModel: SupportCarModel) => {
  const largestType = findLargestSupportCarModel(supportCarModel);
  const t = largestType.split("・");
  const t1 = t[0];
  const t2 = t[1];
  const includePoint = largestType.includes("・");
  return (
    <>
      {t1}
      {includePoint && "・"}
      <br />
      {t2}
    </>
  );
};

const TableRow = ({
  spaceGroup: {
    feature,
    monthlyRent,
    spaceGroupAvailability: availability,
    monthlyOtherFee,
    supportCarModel,
    remarks,
    isPublic,
    id: spaceGroupId,
  },
}: {
  spaceGroup: LessorSpaceGroupsList;
}) => {
  const params = useParams();
  const { totalSpace, emptySpace, fullTemporary, bookedSpace } = availability;
  const monthlyRentValue = calculateMonthlyRent(monthlyOtherFee, monthlyRent?.default?.value);
  return (
    <Link
      href={`/property/${params.propertyId}/space-group/${spaceGroupId}`}
      className="grid min-h-[61px] w-full grid-cols-detail rounded border border-border bg-white py-2 shadow-table hover:border-text-gray"
    >
      <div className={`border-r text-16px ${tailwindPlaceCenter}`}>{convertNumberToLocaleString(monthlyRentValue)}</div>
      <div className={`text ${tailwindPlaceCenter} border-r text-12px`}>
        {createIndention(supportCarModel as SupportCarModel)}
      </div>
      <div className={`text ${tailwindPlaceCenter} border-r text-12px`}>
        {convertParkingType(feature.type as ParkingType)}
      </div>
      <div className={`text ${tailwindPlaceCenter} border-r text-12px`}>{remarks}</div>
      <div className={`text ${tailwindPlaceCenter} border-r text-s`}>{totalSpace + "台"}</div>
      <div
        className={`border-r text-s text-blue ${genClass(emptySpace)}
        `}
      >
        {emptySpace}台
      </div>
      <div
        className={`border-r text-s text-full
        ${genClass(fullTemporary)}
        `}
      >
        {fullTemporary}台
      </div>
      <div className={`border-r text-s text-full ${genClass(bookedSpace)}`}>{bookedSpace}台</div>
      <div className={`${tailwindPlaceCenter}`}>
        <PublicTag isPublic={isPublic} />
      </div>
    </Link>
  );
};

const headers: { name: string; field?: SpaceGroupOrderFields }[] = [
  { name: "月額費用", field: "MONTHLY_RENT" },
  { name: "対応車両" },
  { name: "識別" },
  { name: "備考" },
  { name: "区画数", field: "TOTAL_NUMBER" },
  { name: "空車", field: "EMPTY_NUMBER" },
  { name: "仮押さえ", field: "FULL_TEMPORARY_NUMBER" },
  { name: "満車", field: "BOOKED_SPACE_NUMBER" },
  { name: "公開設定", field: "IS_PUBLIC" },
];

const { ASC, DESC } = SORT_DESTINATION;

const ParkingDetailTable = ({
  spaceGroups,
  propertyId,
  setOrder,
  onResetPagination,
}: {
  spaceGroups?: LessorSpaceGroupsList[];
  propertyId: string;
  setOrder: Dispatch<SetStateAction<SpaceGroupOrder | null>>;
  onResetPagination: () => void;
}) => {
  const [sortDirection, setSortDirection] = useState<SortDestination | "">(""); // 並び替えの方向
  const [sortField, setSortField] = useState<SpaceGroupOrderFields | undefined>(undefined);

  const onSort = (field: SpaceGroupOrderFields) => {
    onResetPagination();
    setSortField(field);
    const newSortDirection = sortField !== field ? DESC : sortDirection === ASC ? DESC : ASC;
    setSortDirection(newSortDirection);
    setOrder(`${field}_${newSortDirection}` as SpaceGroupOrder);
  };

  return (
    <div>
      <div className="grid h-8 grid-cols-detail rounded bg-table-headerBackgroundDark px-2">
        {headers.map(({ name, field }, index) => (
          <ParkingDetailTableSortField
            key={index}
            sortField={sortField}
            field={field}
            name={name}
            onSort={onSort}
            sortDirection={sortDirection}
          />
        ))}
      </div>
      <div className="flex flex-1 flex-col gap-1 pt-2">
        {!spaceGroups ? (
          <div className="flex min-h-[300px] flex-1">
            <FullSizeLoading />
          </div>
        ) : spaceGroups.length ? (
          spaceGroups.map((row, rowIndex) => <TableRow key={rowIndex} spaceGroup={row} />)
        ) : (
          <div className="flex min-h-[257px] flex-1">
            <EmptyTable
              buttonLabel="区画グループを新規登録"
              label="区画グループを登録してください"
              href={`/property/${propertyId}/space-group/create`}
              buttonWidth="208px"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ParkingDetailTable;
